import React, { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import "./InlineEditableField.css";

export interface SelectOption {
  value: string;
  label: string;
}

export interface InlineEditableFieldProps {
  label: string;
  value: string | number | undefined | null;
  type?: "text" | "email" | "tel" | "url" | "number" | "select" | "textarea";
  options?: SelectOption[];
  onChange: (value: string) => void;
  isEditing: boolean;
  error?: string;
  required?: boolean;
  placeholder?: string;
  displayValue?: string; // Custom display when not editing (e.g., for links, formatted values)
  children?: React.ReactNode; // For custom display content (e.g., links)
}

const InlineEditableField: React.FC<InlineEditableFieldProps> = ({
  label,
  value,
  type = "text",
  options = [],
  onChange,
  isEditing,
  error,
  required = false,
  placeholder,
  displayValue,
  children,
}) => {
  const { t } = useTranslation(["common"]);
  const stringValue = value?.toString() ?? "";

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    onChange(e.target.value);
  };

  const renderViewMode = () => {
    if (children) {
      return children;
    }

    if (displayValue !== undefined) {
      return displayValue;
    }

    if (!stringValue) {
      return (
        <span className="not-provided">{t("common:misc.notProvided")}</span>
      );
    }

    // Format display based on type
    if (type === "number" && stringValue) {
      return `$${stringValue}${t("common:misc.perDay")}`;
    }

    if (type === "select" && options.length > 0) {
      const option = options.find((opt) => opt.value === stringValue);
      return (
        option?.label ||
        stringValue || (
          <span className="not-provided">{t("common:misc.notSpecified")}</span>
        )
      );
    }

    return stringValue;
  };

  const renderEditMode = () => {
    const commonProps = {
      value: stringValue,
      onChange: handleChange,
      className: error ? "inline-edit-input error" : "inline-edit-input",
      placeholder:
        placeholder || `${t("common:misc.enter")} ${label.toLowerCase()}...`,
    };

    switch (type) {
      case "select":
        return (
          <select {...commonProps}>
            <option value="">
              {t("common:misc.select")} {label.toLowerCase()}
            </option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case "textarea":
        return <textarea {...commonProps} rows={3} />;

      case "number":
        return <input {...commonProps} type="number" min="0" step="0.01" />;

      default:
        return <input {...commonProps} type={type} />;
    }
  };

  return (
    <div className={`info-item ${isEditing ? "info-item--editing" : ""}`}>
      <span className="label">
        {label}:
        {required && isEditing && <span className="required-star">*</span>}
      </span>
      <span className="value">
        {isEditing ? renderEditMode() : renderViewMode()}
        {error && isEditing && <span className="field-error">{error}</span>}
      </span>
    </div>
  );
};

export default InlineEditableField;
